import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StructuredAddress, formatStructuredAddress, postalFsaPrefix } from '../../lib/storefront';

// ─────────────────────────────────────────────────────────────────────────────
// Shared checkout address form (store page, embed iframe, customer portal).
// Google Places autocomplete on the street line (lazy-loaded on first focus so
// the embed bundle stays slim), with manual field entry always available.
// Optionally shows a live delivery-area check against FSA postal prefixes.
// ─────────────────────────────────────────────────────────────────────────────

export function emptyAddress(): StructuredAddress {
  return { company: '', line1: '', line2: '', city: '', province: '', postal_code: '', country: 'Canada', formatted: '' };
}

/**
 * Wraps a required input and overlays a red asterisk while it's empty
 * (placeholders are single-color, so the mark can't live in the placeholder).
 */
export function RequiredField({ empty, children }: { empty: boolean; children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      {empty && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-red-500 text-base font-bold leading-none"
        >
          *
        </span>
      )}
    </div>
  );
}

export function isAddressComplete(a: StructuredAddress | null): boolean {
  return !!a && !!a.line1.trim() && !!a.city.trim() && postalFsaPrefix(a.postal_code).length === 3;
}

/** null = unknown (no postal yet), true/false = in/out of the delivery areas */
export function addressAreaStatus(a: StructuredAddress | null, prefixes: string[] | null | undefined): boolean | null {
  if (!prefixes) return null;
  const prefix = a ? postalFsaPrefix(a.postal_code) : '';
  if (prefix.length < 3) return null;
  return prefixes.includes(prefix);
}

// Load the Google Maps JS (places) once per page, on demand.
let mapsPromise: Promise<void> | null = null;
function loadGoogleMaps(): Promise<void> {
  if (window.google?.maps?.places) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8';
  mapsPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      mapsPromise = null;
      reject(new Error('maps_load_failed'));
    };
    document.head.appendChild(script);
  });
  return mapsPromise;
}

interface AddressFormProps {
  value: StructuredAddress;
  onChange: (address: StructuredAddress) => void;
  /** className applied to every input (e.g. 'embed-input' or the portal input class) */
  inputClass: string;
  /** Delivery-area FSA prefixes; when provided, a live in/out badge is shown */
  areaPrefixes?: string[] | null;
  /** Accent color for the positive area badge */
  accent?: string;
  idPrefix?: string;
}

export default function AddressForm({ value, onChange, inputClass, areaPrefixes, accent = '#059669', idPrefix = 'addr' }: AddressFormProps) {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const set = useCallback((patch: Partial<StructuredAddress>) => {
    const next = { ...value, ...patch };
    next.formatted = formatStructuredAddress(next);
    onChange(next);
  }, [value, onChange]);

  const ensureMaps = useCallback(async () => {
    try {
      await loadGoogleMaps();
      if (!serviceRef.current && window.google?.maps?.places) {
        serviceRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    } catch {
      // Autocomplete unavailable — manual entry still works.
    }
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (!serviceRef.current || input.trim().length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    serviceRef.current.getPlacePredictions(
      { input, types: ['address'], sessionToken: sessionTokenRef.current || undefined },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  const handleLine1Change = (text: string) => {
    set({ line1: text });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(text), 250);
  };

  const selectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    setShowDropdown(false);
    setPredictions([]);
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      { placeId: prediction.place_id, fields: ['address_components'], sessionToken: sessionTokenRef.current || undefined },
      (place, status) => {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.address_components) return;
        const c: Record<string, string> = {};
        for (const comp of place.address_components) {
          for (const type of comp.types) {
            c[type] = comp.long_name;
            c[`${type}_short`] = comp.short_name;
          }
        }
        const line1 = `${c['street_number'] || ''} ${c['route'] || ''}`.trim() || prediction.structured_formatting.main_text;
        set({
          line1,
          city: c['locality'] || c['postal_town'] || '',
          province: c['administrative_area_level_1_short'] || c['administrative_area_level_1'] || '',
          postal_code: (c['postal_code'] || '').toUpperCase(),
          country: c['country'] || 'Canada',
        });
      }
    );
  };

  // Close the dropdown on outside taps (component-local, iframe-safe).
  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, []);

  const areaStatus = addressAreaStatus(value, areaPrefixes);

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={value.company || ''}
        onChange={e => set({ company: e.target.value })}
        placeholder="Company / business name (optional)"
        autoComplete="organization"
        className={inputClass}
      />

      <div className="relative" ref={wrapRef}>
        <RequiredField empty={!value.line1.trim()}>
          <input
            id={`${idPrefix}-line1`}
            type="text"
            value={value.line1}
            onChange={e => handleLine1Change(e.target.value)}
            onFocus={() => {
              ensureMaps();
              if (predictions.length > 0) setShowDropdown(true);
            }}
            placeholder="Street address"
            autoComplete="address-line1"
            className={inputClass}
          />
        </RequiredField>
        {showDropdown && predictions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-52 overflow-y-auto z-30">
            {predictions.map(p => (
              <button
                key={p.place_id}
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  selectPrediction(p);
                }}
                className="w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
              >
                <span className="block font-medium text-gray-900 truncate">{p.structured_formatting.main_text}</span>
                <span className="block text-xs text-gray-500 truncate">{p.structured_formatting.secondary_text}</span>
              </button>
            ))}
            <div className="px-3 py-1 text-right text-[10px] text-gray-400">powered by Google</div>
          </div>
        )}
      </div>

      <input
        type="text"
        value={value.line2 || ''}
        onChange={e => set({ line2: e.target.value })}
        placeholder="Apt, suite, unit (optional)"
        autoComplete="address-line2"
        className={inputClass}
      />

      <div className="grid grid-cols-2 gap-2">
        <RequiredField empty={!value.city.trim()}>
          <input
            type="text"
            value={value.city}
            onChange={e => set({ city: e.target.value })}
            placeholder="City"
            autoComplete="address-level2"
            className={inputClass}
          />
        </RequiredField>
        <input
          type="text"
          value={value.province}
          onChange={e => set({ province: e.target.value })}
          placeholder="Province / State"
          autoComplete="address-level1"
          className={inputClass}
        />
      </div>

      <div>
        <RequiredField empty={!value.postal_code.trim()}>
          <input
            type="text"
            value={value.postal_code}
            onChange={e => set({ postal_code: e.target.value.toUpperCase() })}
            placeholder="Postal code"
            autoComplete="postal-code"
            className={inputClass}
          />
        </RequiredField>
        {areaStatus === true && (
          <p className="mt-1 text-xs font-medium flex items-center gap-1" style={{ color: accent }}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Great news — we deliver to your area
          </p>
        )}
        {areaStatus === false && (
          <p className="mt-1 text-xs font-medium text-red-600 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Sorry, we don't deliver to this area yet
          </p>
        )}
      </div>
    </div>
  );
}
